import React, { useState, useEffect } from 'react';
import { 
    Users, Settings, BarChart3, FileText, Crown, 
    ChevronRight, TrendingUp, Eye, Heart, MessageCircle,
    UserPlus, UserMinus, Edit, Trash2, CheckCircle,
    XCircle, AlertCircle, Plus, Search, Filter,
    Calendar, Award, Lock, Unlock, ChevronDown
} from 'lucide-react';
import { baseUrl } from '../config';

// =====================================================================
// Styles — dark translucent "glass" theme, scoped under .cm-root.
// Kept in this file; no dependence on Tailwind (its config purges classes).
// =====================================================================
const CM_STYLES = `
.cm-root {
    --cm-teal: #0fb3ad; --cm-teal-hover: #0c9a95; --cm-teal-light: #5eead4;
    --cm-teal-soft: rgba(15,179,173,0.12); --cm-teal-border: rgba(15,179,173,0.45);
    --cm-glass: rgba(20,29,44,0.62); --cm-glass-2: rgba(255,255,255,0.04);
    --cm-border: rgba(255,255,255,0.09); --cm-text: #f3f5f7; --cm-muted: #94a3b8;
    display: flex; flex-direction: column; gap: 20px; color: var(--cm-text);
}
.cm-root button { font-family: inherit; }
.cm-root button:focus-visible, .cm-root input:focus-visible, .cm-root textarea:focus-visible, .cm-root a:focus-visible {
    outline: 2px solid var(--cm-teal-light); outline-offset: 2px;
}

/* Glass surfaces */
.cm-glass {
    background: var(--cm-glass);
    backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
    border: 1px solid var(--cm-border);
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.28);
}
.cm-pad { padding: 22px 24px; }

/* Header */
.cm-header { position: relative; overflow: hidden; display: flex; align-items: center; justify-content: space-between; gap: 16px;
    padding: 24px 28px;
    background: linear-gradient(120deg, rgba(15,179,173,0.28) 0%, rgba(20,29,44,0.7) 55%, rgba(20,29,44,0.62) 100%); }
.cm-header-main { display: flex; align-items: center; gap: 16px; min-width: 0; }
.cm-header-icon { width: 52px; height: 52px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    border-radius: 12px; background: rgba(15,179,173,0.2); border: 1px solid var(--cm-teal-border); color: var(--cm-teal-light); }
.cm-header-title { margin: 0; font-size: 24px; font-weight: 700; color: #fff; }
.cm-header-sub { margin: 4px 0 0; font-size: 14px; color: #cbd5e1; }
.cm-header-art { color: var(--cm-teal-light); opacity: .35; flex-shrink: 0; }

/* Section labels */
.cm-label { display: block; margin: 0 0 12px; font-size: 13px; font-weight: 600; color: var(--cm-muted); }
.cm-h3 { margin: 0 0 14px; font-size: 17px; font-weight: 600; color: var(--cm-text); }

/* Community selector */
.cm-community-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.cm-community { display: flex; align-items: center; gap: 12px; padding: 14px 16px; text-align: left; cursor: pointer;
    background: var(--cm-glass-2); border: 1px solid var(--cm-border); border-radius: 12px; color: var(--cm-text);
    transition: border-color .15s ease, background-color .15s ease; }
.cm-community:hover { border-color: var(--cm-teal-border); }
.cm-community.is-active { background: var(--cm-teal-soft); border-color: var(--cm-teal); }
.cm-community-logo { width: 46px; height: 46px; flex-shrink: 0; border-radius: 10px; object-fit: cover; }
.cm-community-initial { width: 46px; height: 46px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    border-radius: 10px; background: var(--cm-teal); color: #fff; font-size: 19px; font-weight: 700; }
.cm-community-text { min-width: 0; }
.cm-community-name { margin: 0; font-size: 15px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cm-community-role { display: flex; align-items: center; gap: 5px; margin: 3px 0 0; font-size: 12px; color: var(--cm-teal-light); }

/* Stats */
.cm-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.cm-stat { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 20px 22px;
    border-left: 3px solid var(--cm-teal); }
.cm-stat-label { margin: 0; font-size: 13px; color: var(--cm-muted); font-weight: 500; }
.cm-stat-value { margin: 6px 0 0; font-size: 28px; font-weight: 700; color: #fff; line-height: 1.1; }
.cm-stat-icon { width: 46px; height: 46px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 12px; }
.cm-tone-teal  { background: rgba(15,179,173,0.16); color: #5eead4; }
.cm-tone-sky   { background: rgba(14,165,233,0.16); color: #7dd3fc; }
.cm-tone-amber { background: rgba(245,158,11,0.16); color: #fcd34d; }
.cm-tone-green { background: rgba(16,185,129,0.16); color: #6ee7b7; }

/* Tabs */
.cm-tabs { display: flex; gap: 4px; padding: 0 12px; border-bottom: 1px solid var(--cm-border); overflow-x: auto; }
.cm-tab { display: flex; align-items: center; gap: 8px; padding: 15px 16px; margin-bottom: -1px; border: none;
    border-bottom: 2px solid transparent; background: none; color: var(--cm-muted); font-size: 14px; font-weight: 600;
    white-space: nowrap; cursor: pointer; transition: color .15s ease, border-color .15s ease; }
.cm-tab:hover { color: #fff; }
.cm-tab.is-active { color: var(--cm-teal-light); border-bottom-color: var(--cm-teal); }
.cm-tab-body { padding: 24px; }

/* Overview */
.cm-health { padding: 20px 22px; margin-bottom: 24px; border-radius: 14px; background: rgba(15,179,173,0.08); border: 1px solid var(--cm-teal-border); }
.cm-health-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.cm-health-label { margin: 0; font-size: 13px; color: var(--cm-muted); }
.cm-health-row { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
.cm-progress { flex: 1; height: 8px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; }
.cm-progress-bar { height: 100%; border-radius: 999px; background: var(--cm-teal); transition: width .3s ease; }
.cm-strong { font-size: 14px; font-weight: 700; color: #fff; }
.cm-green { color: #6ee7b7; }
.cm-activity { display: flex; flex-direction: column; gap: 10px; }
.cm-activity-item { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 12px;
    background: var(--cm-glass-2); border: 1px solid var(--cm-border); }
.cm-activity-icon { width: 38px; height: 38px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    border-radius: 10px; background: var(--cm-teal-soft); color: var(--cm-teal-light); }
.cm-activity-text { margin: 0; font-size: 14px; color: var(--cm-text); }
.cm-activity-time { margin: 2px 0 0; font-size: 12px; color: var(--cm-muted); }
.cm-empty-text { margin: 0; padding: 32px 0; text-align: center; font-size: 14px; color: var(--cm-muted); }

/* Inputs */
.cm-root .cm-input { display: block; width: 100%; padding: 11px 14px;
    background-color: rgba(255,255,255,0.05); background-image: none;
    border: 1px solid rgba(255,255,255,0.14); border-radius: 10px; box-shadow: none;
    color: #f3f5f7; -webkit-text-fill-color: #f3f5f7; caret-color: #f3f5f7;
    font-size: 14px; line-height: 1.5; outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
.cm-root .cm-input::placeholder { color: #7c8a9e; -webkit-text-fill-color: #7c8a9e; opacity: 1; }
.cm-root .cm-input:hover { border-color: rgba(255,255,255,0.22); }
.cm-root .cm-input:focus { border-color: var(--cm-teal); box-shadow: 0 0 0 3px rgba(15,179,173,0.2); outline: none; }
.cm-root .cm-input:-webkit-autofill,
.cm-root .cm-input:-webkit-autofill:hover,
.cm-root .cm-input:-webkit-autofill:focus {
    -webkit-text-fill-color: #f3f5f7; -webkit-box-shadow: 0 0 0 1000px #172234 inset; box-shadow: 0 0 0 1000px #172234 inset;
}
.cm-root .cm-input--search { padding-left: 40px; }
.cm-root .cm-textarea { min-height: 120px; resize: vertical; }
.cm-search { position: relative; margin-bottom: 16px; }
.cm-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--cm-muted); pointer-events: none; }
.cm-field { margin-bottom: 20px; }
.cm-field-label { display: block; margin-bottom: 8px; font-size: 14px; font-weight: 600; color: var(--cm-text); }
.cm-check { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-size: 14px; color: var(--cm-text); cursor: pointer; }
.cm-check input { width: 18px; height: 18px; accent-color: var(--cm-teal); cursor: pointer; }

/* Buttons */
.cm-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 44px; padding: 0 24px;
    border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none;
    transition: background-color .15s ease; }
.cm-btn:disabled { opacity: .55; cursor: not-allowed; }
.cm-btn-primary { background: var(--cm-teal); color: #fff; }
.cm-btn-primary:hover:not(:disabled) { background: var(--cm-teal-hover); }
.cm-link-danger { padding: 0; border: none; background: none; color: #fca5a5; font-size: 13px; font-weight: 600; cursor: pointer; }
.cm-link-danger:hover:not(:disabled) { color: #fecaca; }
.cm-link-danger:disabled { opacity: .5; cursor: not-allowed; }
.cm-footer { display: flex; justify-content: flex-end; margin-top: 8px; padding-top: 20px; border-top: 1px solid var(--cm-border); }

/* Members table */
.cm-table-wrap { border: 1px solid var(--cm-border); border-radius: 12px; overflow-x: auto; }
.cm-table { width: 100%; min-width: 640px; border-collapse: collapse; font-size: 14px; }
.cm-table th { padding: 12px 18px; text-align: left; font-size: 12px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase;
    color: var(--cm-muted); background: rgba(255,255,255,0.03); border-bottom: 1px solid var(--cm-border); }
.cm-table td { padding: 14px 18px; border-bottom: 1px solid var(--cm-border); color: var(--cm-text); vertical-align: middle; }
.cm-table tbody tr:last-child td { border-bottom: none; }
.cm-table tbody tr:hover td { background: rgba(255,255,255,0.02); }
.cm-table-empty { text-align: center; color: var(--cm-muted) !important; padding: 32px 18px !important; }
.cm-member { display: flex; align-items: center; gap: 12px; }
.cm-avatar { width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; object-fit: cover; }
.cm-avatar-initial { width: 40px; height: 40px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
    border-radius: 50%; background: var(--cm-teal); color: #fff; font-weight: 700; }
.cm-member-name { margin: 0; font-weight: 600; }
.cm-member-email { margin: 2px 0 0; font-size: 13px; color: var(--cm-muted); }
.cm-muted { color: var(--cm-muted); font-size: 13px; }
.cm-role { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; text-transform: capitalize;
    background: rgba(148,163,184,0.14); color: #cbd5e1; }
.cm-role--admin { background: rgba(245,158,11,0.14); color: #fcd34d; }

/* Posts */
.cm-posts { display: flex; flex-direction: column; gap: 14px; }
.cm-post { display: flex; gap: 16px; padding: 16px; border-radius: 12px; background: var(--cm-glass-2); border: 1px solid var(--cm-border);
    transition: border-color .15s ease; }
.cm-post:hover { border-color: var(--cm-teal-border); }
.cm-post-thumb { width: 96px; height: 96px; flex-shrink: 0; border-radius: 10px; object-fit: cover; }
.cm-post-thumb--placeholder { display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.06); color: var(--cm-muted); }
.cm-post-body { flex: 1; min-width: 0; }
.cm-post-title { margin: 0 0 4px; font-size: 15px; font-weight: 700; color: #fff; }
.cm-post-content { margin: 0 0 12px; font-size: 14px; line-height: 1.5; color: #cbd5e1;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.cm-post-meta { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: var(--cm-muted); }
.cm-post-meta span { display: inline-flex; align-items: center; gap: 5px; }
.cm-posts-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 40px 0; color: var(--cm-muted); font-size: 14px; }

/* Empty state (no communities) */
.cm-empty { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 48px 24px; }
.cm-empty-icon { width: 72px; height: 72px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
    border-radius: 18px; background: var(--cm-teal-soft); color: var(--cm-teal-light); }
.cm-empty-title { margin: 0; font-size: 20px; font-weight: 700; color: #fff; }
.cm-empty-msg { margin: 8px 0 22px; max-width: 420px; font-size: 14px; line-height: 1.55; color: var(--cm-muted); }

/* Loading */
.cm-loading { display: flex; align-items: center; justify-content: center; height: 256px; }
.cm-spinner { width: 44px; height: 44px; border-radius: 50%; border: 4px solid rgba(255,255,255,0.1); border-top-color: var(--cm-teal);
    animation: cm-spin .8s linear infinite; }
@keyframes cm-spin { to { transform: rotate(360deg); } }

/* Responsive */
@media (max-width: 1100px) { .cm-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 768px) {
    .cm-header { padding: 20px; }
    .cm-header-art { display: none; }
    .cm-header-title { font-size: 20px; }
    .cm-pad { padding: 18px 16px; }
    .cm-stats { grid-template-columns: 1fr; }
    .cm-tab-body { padding: 18px 16px; }
    .cm-health-grid { grid-template-columns: 1fr; }
    .cm-post { flex-wrap: wrap; }
}
@media (prefers-reduced-motion: reduce) {
    .cm-spinner { animation: none; }
    .cm-community, .cm-tab, .cm-btn, .cm-post, .cm-progress-bar { transition: none; }
}
`;

const CommunityManager = () => {
    const [adminCommunities, setAdminCommunities] = useState([]);
    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [members, setMembers] = useState([]);
    const [posts, setPosts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // ✅ Fetch communities where user is admin
    useEffect(() => {
        fetchAdminCommunities();
    }, []);

    // ✅ Fetch selected community data
    useEffect(() => {
        if (selectedCommunity) {
            fetchCommunityStats();
            if (activeTab === 'members') fetchMembers();
            if (activeTab === 'posts') fetchPosts();
        }
    }, [selectedCommunity, activeTab]);

    const fetchAdminCommunities = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("access_token");
            
            const response = await fetch(`${baseUrl}/community/my-communities`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // Filter only communities where user is admin/creator
                const adminComms = data.filter(c => 
                    c.is_admin || c.role === 'admin' || c.role === 'creator'
                );
                setAdminCommunities(adminComms);
                
                // Auto-select first community if available
                if (adminComms.length > 0 && !selectedCommunity) {
                    setSelectedCommunity(adminComms[0]);
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const fetchCommunityStats = async () => {
        try {
            const token = localStorage.getItem("access_token");
            
            // Fetch community details
            const communityResponse = await fetch(
                `${baseUrl}/community/${selectedCommunity.community_id}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (communityResponse.ok) {
                const communityData = await communityResponse.json();
                
                // Fetch posts for this community
                const postsResponse = await fetch(
                    `${baseUrl}/community-post/list/by-community/${selectedCommunity.community_id}`,
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );
                
                let postsData = { posts: [] };
                if (postsResponse.ok) {
                    postsData = await postsResponse.json();
                }
                
                // Calculate stats
                const posts = postsData.posts || postsData || [];
                const totalViews = posts.reduce((sum, p) => sum + (p.view_count || 0), 0);
                const totalLikes = posts.reduce((sum, p) => sum + (p.like_count || 0), 0);
                const totalComments = posts.reduce((sum, p) => sum + (p.comment_count || 0), 0);
                
                setStats({
                    memberCount: communityData.member_count || 0,
                    postCount: posts.length,
                    totalViews,
                    totalLikes,
                    totalComments,
                    engagement: posts.length > 0 ? ((totalLikes + totalComments) / posts.length).toFixed(1) : 0
                });
            }
        } catch (error) {
        }
    };

    const fetchMembers = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(
                `${baseUrl}/community/${selectedCommunity.community_id}/members`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setMembers(data);
            }
        } catch (error) {
            setMembers([]);
        }
    };

    const fetchPosts = async () => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(
                `${baseUrl}/community-post/list/by-community/${selectedCommunity.community_id}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setPosts(data.posts || data || []);
            }
        } catch (error) {
            setPosts([]);
        }
    };

    // ============ NO ADMIN COMMUNITIES VIEW ============
    if (loading) {
        return (
            <div className="cm-root">
                <style>{CM_STYLES}</style>
                <div className="cm-loading"><div className="cm-spinner" /></div>
            </div>
        );
    }

    if (adminCommunities.length === 0) {
        return (
            <div className="cm-root">
                <style>{CM_STYLES}</style>
                <div className="cm-glass cm-empty">
                    <span className="cm-empty-icon"><Users size={34} /></span>
                    <h3 className="cm-empty-title">No Communities to Manage</h3>
                    <p className="cm-empty-msg">
                        You are not an admin of any communities yet. Create or join a community to get started!
                    </p>
                    <a href="/community" className="cm-btn cm-btn-primary">
                        <Users size={18} />
                        Browse Communities
                    </a>
                </div>
            </div>
        );
    }

    // ============ MAIN COMMUNITY MANAGER INTERFACE ============
    return (
        <div className="cm-root">
            <style>{CM_STYLES}</style>

            {/* Header */}
            <div className="cm-glass cm-header">
                <div className="cm-header-main">
                    <span className="cm-header-icon"><Crown size={26} /></span>
                    <div>
                        <h1 className="cm-header-title">Community Management</h1>
                        <p className="cm-header-sub">
                            Manage your {adminCommunities.length} communit{adminCommunities.length === 1 ? 'y' : 'ies'}
                        </p>
                    </div>
                </div>
                <Award size={60} className="cm-header-art" />
            </div>

            {/* Community Selector */}
            <div className="cm-glass cm-pad">
                <span className="cm-label">Select community to manage</span>
                <div className="cm-community-grid">
                    {adminCommunities.map((community) => (
                        <button
                            key={community.community_id}
                            type="button"
                            onClick={() => setSelectedCommunity(community)}
                            className={`cm-community ${selectedCommunity?.community_id === community.community_id ? 'is-active' : ''}`}
                            aria-pressed={selectedCommunity?.community_id === community.community_id}
                        >
                            {community.logo_url ? (
                                <img src={community.logo_url} alt={community.name} className="cm-community-logo" />
                            ) : (
                                <div className="cm-community-initial">{community.name.charAt(0)}</div>
                            )}
                            <div className="cm-community-text">
                                <h3 className="cm-community-name">{community.name}</h3>
                                <p className="cm-community-role">
                                    <Crown size={12} />
                                    {community.role === 'creator' ? 'Creator' : 'Admin'}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Community Management Dashboard */}
            {selectedCommunity && (
                <>
                    {/* Stats Overview */}
                    <div className="cm-stats">
                        <StatCard icon={Users} label="Total Members" value={stats?.memberCount || 0} color="teal" />
                        <StatCard icon={FileText} label="Total Posts" value={stats?.postCount || 0} color="sky" />
                        <StatCard icon={Eye} label="Total Views" value={stats?.totalViews || 0} color="amber" />
                        <StatCard icon={TrendingUp} label="Avg Engagement" value={stats?.engagement || 0} color="green" />
                    </div>

                    {/* Tabs */}
                    <div className="cm-glass">
                        <nav className="cm-tabs" role="tablist" aria-label="Community sections">
                            <TabButton icon={BarChart3} label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                            <TabButton icon={Users} label="Members" active={activeTab === 'members'} onClick={() => setActiveTab('members')} />
                            <TabButton icon={FileText} label="Posts" active={activeTab === 'posts'} onClick={() => setActiveTab('posts')} />
                            <TabButton icon={Settings} label="Settings" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
                        </nav>

                        <div className="cm-tab-body">
                            {activeTab === 'overview' && (
                                <OverviewTab stats={stats} community={selectedCommunity} />
                            )}
                            {activeTab === 'members' && (
                                <MembersTab 
                                    members={members} 
                                    communityId={selectedCommunity.community_id}
                                    onRefresh={fetchMembers}
                                />
                            )}
                            {activeTab === 'posts' && (
                                <PostsTab 
                                    posts={posts} 
                                    communityId={selectedCommunity.community_id}
                                    onRefresh={fetchPosts}
                                />
                            )}
                            {activeTab === 'settings' && (
                                <SettingsTab 
                                    community={selectedCommunity}
                                    onUpdate={fetchAdminCommunities}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ============ SUB-COMPONENTS ============

// Stat Card
const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="cm-glass cm-stat">
        <div>
            <p className="cm-stat-label">{label}</p>
            <p className="cm-stat-value">{value.toLocaleString()}</p>
        </div>
        <span className={`cm-stat-icon cm-tone-${color}`}>
            <Icon size={22} />
        </span>
    </div>
);

// Tab Button
const TabButton = ({ icon: Icon, label, active, onClick }) => (
    <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        className={`cm-tab ${active ? 'is-active' : ''}`}
    >
        <Icon size={18} />
        {label}
    </button>
);

// Overview Tab
const OverviewTab = ({ stats, community }) => (
    <div>
        <div className="cm-health">
            <h3 className="cm-h3">Community Health</h3>
            <div className="cm-health-grid">
                <div>
                    <p className="cm-health-label">Engagement Rate</p>
                    <div className="cm-health-row">
                        <div className="cm-progress">
                            <div
                                className="cm-progress-bar"
                                style={{ width: `${Math.min((stats?.engagement || 0) * 10, 100)}%` }}
                            />
                        </div>
                        <span className="cm-strong">{stats?.engagement || 0}</span>
                    </div>
                </div>
                <div>
                    <p className="cm-health-label">Activity Level</p>
                    <div className="cm-health-row">
                        <TrendingUp size={20} className="cm-green" />
                        <span className="cm-strong">
                            {stats?.postCount > 10 ? 'High' : stats?.postCount > 5 ? 'Medium' : 'Low'}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <h3 className="cm-h3">Recent Activity</h3>
            <div className="cm-activity">
                {stats?.postCount > 0 ? (
                    <ActivityItem
                        icon={Heart}
                        text={`${stats.totalLikes} total likes across all posts`}
                        time="Overall"
                    />
                ) : null}
                {stats?.totalComments > 0 ? (
                    <ActivityItem
                        icon={MessageCircle}
                        text={`${stats.totalComments} comments from community members`}
                        time="Overall"
                    />
                ) : null}
                {stats?.memberCount > 0 ? (
                    <ActivityItem
                        icon={Users}
                        text={`${stats.memberCount} active members`}
                        time="Current"
                    />
                ) : null}
                {!stats || (stats.postCount === 0 && stats.memberCount === 0) ? (
                    <p className="cm-empty-text">No activity yet. Invite members to get started!</p>
                ) : null}
            </div>
        </div>
    </div>
);

const ActivityItem = ({ icon: Icon, text, time }) => (
    <div className="cm-activity-item">
        <span className="cm-activity-icon"><Icon size={18} /></span>
        <div>
            <p className="cm-activity-text">{text}</p>
            <p className="cm-activity-time">{time}</p>
        </div>
    </div>
);

// Members Tab
const MembersTab = ({ members, communityId, onRefresh }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [removingMember, setRemovingMember] = useState(null);

    const handleRemoveMember = async (userId) => {
        if (!window.confirm('Are you sure you want to remove this member?')) return;

        try {
            setRemovingMember(userId);
            const token = localStorage.getItem("access_token");
            const response = await fetch(
                `${baseUrl}/community/${communityId}/remove-member/${userId}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.ok) {
                alert('Member removed successfully');
                onRefresh();
            } else {
                alert('Failed to remove member');
            }
        } catch (error) {
            alert('Error removing member');
        } finally {
            setRemovingMember(null);
        }
    };

    const filteredMembers = members.filter(m =>
        m.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div>
            {/* Search */}
            <div className="cm-search">
                <Search size={17} className="cm-search-icon" />
                <input
                    type="text"
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="cm-input cm-input--search"
                    aria-label="Search members"
                />
            </div>

            {/* Members List */}
            <div className="cm-table-wrap">
                <table className="cm-table">
                    <thead>
                        <tr>
                            <th>Member</th>
                            <th>Role</th>
                            <th>Joined</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMembers.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="cm-table-empty">No members found</td>
                            </tr>
                        ) : (
                            filteredMembers.map((member) => (
                                <tr key={member.user_id}>
                                    <td>
                                        <div className="cm-member">
                                            {member.profile_picture ? (
                                                <img src={member.profile_picture} alt={member.username} className="cm-avatar" />
                                            ) : (
                                                <div className="cm-avatar-initial">{member.username?.charAt(0) || '?'}</div>
                                            )}
                                            <div>
                                                <p className="cm-member-name">{member.username || 'Unknown'}</p>
                                                <p className="cm-member-email">{member.email || 'No email'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`cm-role ${member.role === 'admin' || member.role === 'creator' ? 'cm-role--admin' : ''}`}>
                                            {member.role || 'Member'}
                                        </span>
                                    </td>
                                    <td className="cm-muted">
                                        {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'Unknown'}
                                    </td>
                                    <td>
                                        {member.role !== 'creator' && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveMember(member.user_id)}
                                                disabled={removingMember === member.user_id}
                                                className="cm-link-danger"
                                            >
                                                {removingMember === member.user_id ? 'Removing...' : 'Remove'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Posts Tab
const PostsTab = ({ posts, communityId, onRefresh }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [deletingPost, setDeletingPost] = useState(null);

    const handleDeletePost = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;

        try {
            setDeletingPost(postId);
            const token = localStorage.getItem("access_token");
            const response = await fetch(
                `${baseUrl}/community-post/${postId}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.ok) {
                alert('Post deleted successfully');
                onRefresh();
            } else {
                alert('Failed to delete post');
            }
        } catch (error) {
            alert('Error deleting post');
        } finally {
            setDeletingPost(null);
        }
    };

    const filteredPosts = posts.filter(p =>
        p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.content?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div>
            {/* Search */}
            <div className="cm-search">
                <Search size={17} className="cm-search-icon" />
                <input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="cm-input cm-input--search"
                    aria-label="Search posts"
                />
            </div>

            {/* Posts */}
            <div className="cm-posts">
                {filteredPosts.length === 0 ? (
                    <div className="cm-posts-empty">
                        <FileText size={48} />
                        <p>No posts found</p>
                    </div>
                ) : (
                    filteredPosts.map((post) => (
                        <div key={post.community_post_id} className="cm-post">
                            {/* Thumbnail */}
                            {post.thumbnail_url && (
                                <img src={post.thumbnail_url} alt={post.title} className="cm-post-thumb" />
                            )}
                            {post.media_type === 'video' && post.video_url && !post.thumbnail_url && (
                                <div className="cm-post-thumb cm-post-thumb--placeholder">
                                    <FileText size={30} />
                                </div>
                            )}

                            {/* Content */}
                            <div className="cm-post-body">
                                <h4 className="cm-post-title">{post.title}</h4>
                                <p className="cm-post-content">{post.content}</p>
                                <div className="cm-post-meta">
                                    <span><Eye size={15} />{post.view_count || 0}</span>
                                    <span><Heart size={15} />{post.like_count || 0}</span>
                                    <span><MessageCircle size={15} />{post.comment_count || 0}</span>
                                    <span><Calendar size={15} />{new Date(post.created_at).toLocaleDateString()}</span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => handleDeletePost(post.community_post_id)}
                                    disabled={deletingPost === post.community_post_id}
                                    className="cm-link-danger"
                                >
                                    {deletingPost === post.community_post_id ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// Settings Tab
const SettingsTab = ({ community, onUpdate }) => {
    const [formData, setFormData] = useState({
        name: community.name || '',
        description: community.description || '',
        is_featured: community.is_featured || false,
        is_trending: community.is_trending || false,
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem("access_token");
            
            const response = await fetch(
                `${baseUrl}/community/${community.community_id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                }
            );

            if (response.ok) {
                alert('Community settings updated successfully!');
                onUpdate();
            } else {
                alert('Failed to update settings');
            }
        } catch (error) {
            alert('Error updating settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            {/* Name */}
            <div className="cm-field">
                <label htmlFor="cm-name" className="cm-field-label">Community Name</label>
                <input
                    id="cm-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="cm-input"
                />
            </div>

            {/* Description */}
            <div className="cm-field">
                <label htmlFor="cm-description" className="cm-field-label">Description</label>
                <textarea
                    id="cm-description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="cm-input cm-textarea"
                />
            </div>

            {/* Toggles */}
            <div className="cm-field">
                <label className="cm-check">
                    <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleChange}
                    />
                    Featured Community
                </label>
                <label className="cm-check">
                    <input
                        type="checkbox"
                        name="is_trending"
                        checked={formData.is_trending}
                        onChange={handleChange}
                    />
                    Trending Community
                </label>
            </div>

            {/* Save Button */}
            <div className="cm-footer">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="cm-btn cm-btn-primary"
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export default CommunityManager;